// Video greeting from the honeymoon — opens whenever someone arrives through a
// thank-you card QR link. The clip is an ordinary item in its own gallery, so it
// goes through the same auth, transcode and download paths as everything else, and
// guests who closed this popup find it again on the home page. No ready video yet
// (still transcoding, or the gallery is empty) → the popup closes itself unseen.
import React, { useEffect, useRef, useState } from 'react';
import Modal from './modal.jsx';
import { displayUrl, fetchMedia, posterUrl } from '../api.js';
import { GREETING } from '../text.js';
import { ExpandIcon, PlayIcon } from './icons.jsx';

export default function Greeting({ onClose }) {
	const [clip, setClip] = useState(null);
	const [started, setStarted] = useState(false);
	const videoRef = useRef(null);

	useEffect(() => {
		let cancelled = false;
		fetchMedia(GREETING.category)
			.then((res) => {
				if (cancelled) return;
				const ready = res.items?.find((i) => i.type === 'video' && i.status === 'ready');
				if (ready) setClip(ready);
				else onClose();
			})
			.catch(() => {
				if (!cancelled) onClose();
			});
		return () => {
			cancelled = true;
		};
	}, [onClose]);

	// iPhone: back to inline playback after leaving native fullscreen (see below).
	// React has no prop for this WebKit-only event.
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return undefined;
		const inline = () => video.setAttribute('playsinline', '');
		video.addEventListener('webkitendfullscreen', inline);
		return () => video.removeEventListener('webkitendfullscreen', inline);
	}, [clip]);

	const play = () => {
		setStarted(true);
		videoRef.current?.play().catch(() => {});
	};

	// The Fullscreen API covers desktop, Android and iPad. iPhone Safari only lets the
	// <video> element itself go fullscreen (webkitEnterFullscreen), and that throws
	// until metadata is loaded — dropping playsinline and playing has the same effect.
	const fullscreen = () => {
		const video = videoRef.current;
		if (!video) return;
		if (video.requestFullscreen) {
			video.requestFullscreen().catch(() => {});
			video.play().catch(() => {});
		} else if (video.webkitRequestFullscreen) {
			video.webkitRequestFullscreen();
			video.play().catch(() => {});
		} else {
			try {
				video.webkitEnterFullscreen();
				video.play().catch(() => {});
			} catch {
				video.removeAttribute('playsinline');
				video.play().catch(() => {});
			}
		}
	};

	if (!clip) return null;

	return (
		<Modal label={GREETING.title} onClose={onClose}>
			<div className='greeting'>
				<h2 className='greeting__title'>{GREETING.title}</h2>
				<p className='greeting__hint'>{GREETING.hint}</p>
				<div className='greeting__stage' style={{ aspectRatio: `${clip.w ?? 9} / ${clip.h ?? 16}` }}>
					{/* Native controls only once it plays: before that iOS paints its own grey
					    play glyph over the poster, which guests did not read as "tap me". */}
					<video
						ref={videoRef}
						className='greeting__video'
						src={displayUrl(clip)}
						// Media URLs are cached immutably; the version skips the blank first-frame
						// poster on devices that already opened this popup.
						poster={`${posterUrl(clip)}?v=2`}
						controls={started}
						playsInline
						preload='metadata'
						onPlay={() => setStarted(true)}
					/>
					{!started && (
						<button type='button' className='greeting__play' onClick={play}>
							<span className='greeting__play-icon'>
								<PlayIcon size={34} />
							</span>
							<span className='greeting__play-label'>{GREETING.play}</span>
						</button>
					)}
				</div>
				<div className='greeting__actions'>
					<button type='button' className='btn btn--primary' onClick={fullscreen}>
						<ExpandIcon size={16} />
						{GREETING.fullscreen}
					</button>
					<button type='button' className='btn btn--ghost' onClick={onClose}>
						{GREETING.close}
					</button>
				</div>
			</div>
		</Modal>
	);
}
